import { Collection } from "../Collection";
import { IInput } from "./Input";
import { SimpleSignal } from "../SimpleSignal";
import { Main } from "../../Main";
import { ObjectUtils } from "../data/ObjectUtils";
import { Templating } from "../Templating";
import { Project } from "../../auth/Project";

export interface IDropDownInputConfig<T>
{
	id?: string;
	tpl?: {
		element: string;
		selected?: string;
		button?: string;
		disclaimer?: string;
		list: string;
		option: string;
	};
	values?: T[];
	valuesCollection?: Collection<T>
	value?: string;
	idProperty?:string;
}

export class DropDownInput<T> implements IInput
{

	public config:IDropDownInputConfig<T>;
	public id:string;
	public changeSignal:SimpleSignal;

	public _value:T;
	public _values:Collection<T>;

	public element:HTMLElement;
	private _button:HTMLElement;
	private _buttonIsSelected:boolean = false;
	private _list:HTMLElement;
	private _selectedOption:HTMLElement;
	private _disclaimer:HTMLElement;

	public userData: any = {};

	public static defaultConfig = {
		tpl: {
			element: '<div class="utils-select-element">{{selected}}{{list}}</div>',
			button: '<div class="utils-select-button">{{value}}</div>',
			list: '<div class="utils-select-list" data-list="true">{{options}}</div>',
			option: '<div class="utils-select-option">{{value}}</div>'
		},
		idProperty: "id"
	};

	constructor(config?:IDropDownInputConfig<T>)
	{
		this.config = ObjectUtils.mergeConfig<IDropDownInputConfig<T>>(DropDownInput.defaultConfig, config);

		this.id = config.id;
		this.changeSignal = new SimpleSignal();

		this._values = config.valuesCollection || new Collection(config.values, config.idProperty);

		this.initGUI();

		if (config.value !== undefined)
		{
			this.setValueByKey(config.value);
		}
	}

	public hide()
	{
		this.element.parentElement.classList.add("hidden");
	}

	public show()
	{
		this.element.parentElement.classList.remove("hidden");
	}

	private initGUI()
	{

		var tpl = this.config.tpl;

		var optionTpl = tpl.option;
		var optionsHTML = "";

		var valuesArray = this._values.array;

		for (var i = 0, ln = valuesArray.length; i < ln; i++)
		{
			var value = valuesArray[i];

			var optionHTML = this.renderOptionHTML(optionTpl, value);
			optionsHTML += optionHTML;
		}

		var disclaimerHTML = tpl.disclaimer ? Templating.render(tpl.disclaimer, {message: 'NOTE: Actual colors may vary.<span id="disclaimerDetailsButton">?</span>'}) : undefined;
		var listHTML = Templating.render(tpl.list, {options: optionsHTML});
		var buttonHTML = Templating.render(tpl.button, {value: this._value});
		this.element = Templating.renderDOM(tpl.element, {button: buttonHTML, list: listHTML, disclaimer: disclaimerHTML});

		this._list = <HTMLElement>this.element.querySelector('[data-list="true"]');
		this._disclaimer = <HTMLElement>this.element.querySelector('[data-disclaimer="true"]');
		this._list.addEventListener("click", this.onListClicked);

		this._button = <HTMLElement>this.element.querySelector('[data-button]');
		this._button.addEventListener("click", this.onButtonClicked);
		if (this._button.getAttribute("data-button") === "selected")
		{
			this._buttonIsSelected = true;
		}

		this._list.addEventListener("mouseover", this.onListMouseOverOut);
		//this._list.addEventListener("mouseout", this.onListMouseOverOut);
	}

	private renderOptionHTML(optionTpl:string, value:T)
	{

		var itemsHTML = "";

		var items = value['items'];
		if (items)
		{

			for (var i = 0; i < items.length; i++)
			{
				var itemValue = items[i];
				itemsHTML += this.renderOptionHTML(optionTpl, itemValue);
			}
		}

		value['nested'] = itemsHTML;

		return Templating.render(optionTpl, value);
	}


	private onListMouseOverOut = (event:MouseEvent) =>
	{

		var show = event.type === "mouseover";

		var target = <HTMLElement>this.findParent(event.target, "data-value");
		if (!target)
		{
			return "";
		}

		// check if it has items, if yes -> show nested items

		var nested = <HTMLElement>target.querySelector("div.addon-nested");
		if (nested)
		{
			var items = nested.querySelectorAll("div.addon-nested > div.addon-option");
		}

		if (items && items.length > 0)
		{

			this.hideAllNestedOptions();

			target.style.position = "relative"; // could be done at init

			if (show)
			{
				requestAnimationFrame(() =>
				{
					nested.style.display = "";
					nested.style.visibility = "hidden";
					nested.style.top = "";
					nested.style.bottom = "-1px";

					requestAnimationFrame(() =>
					{
						const rect = nested.getBoundingClientRect();

						if (rect.top < 0)
						{
							nested.style.top = "-5px";
							nested.style.bottom = "";
						}

						nested.style.visibility = "";
					});
				});
			}
			else
			{
				nested.style.display = "none";
			}
		}
	};

	private hideAllNestedOptions()
	{
		var nestedOptions = this._list.querySelectorAll("div.addon-nested");

		for (var i = 0; i < nestedOptions.length; i++)
		{
			(<HTMLElement>nestedOptions[i]).style['display'] = "none";
		}
	}

	private onButtonClicked = (event:MouseEvent) =>
	{
		this.openList();

		// if clicking on anything other than the dropdown -> hide again

		this._list.addEventListener("mousedown", this.onListMouseDown);
		window.addEventListener("mousedown", this.onWindowMouseDown);
		window.addEventListener("blur", this.onWindowBlur);
	};

	private onListMouseDown = (event:MouseEvent) =>
	{
		// don't let this event reach onWindowMouseDown
		// (with this we allow clicking inside the dropdown without closing it,
		// which is important because there is a scrollbar)
		event.stopPropagation();
	};

	private onWindowMouseDown = (event:MouseEvent) =>
	{
		this.onCloseList(event);
	};

	private onWindowBlur = (event:FocusEvent) =>
	{
		this.onCloseList();
	};

	private closeDisclaimerDetails()
	{
		const divs = document.getElementsByClassName('disclaimerDetails');
		for (let i = divs.length - 1; i >= 0; --i)
		{
			divs[i].parentElement.removeChild(divs[i]);
		}
	}

	private showDisclaimerDetails()
	{
		const div = document.createElement('div');
		div.classList.add('disclaimerDetails')
		const closeButton = document.createElement('div');
		closeButton.classList.add('disclaimerDetailsCloseButton');

		div.innerHTML = 'Please contact us to get the most accurate color representation.<br>' +
			'We will be glad to provide you with color samples. Galvalume has a protective top coat which may possibly change color giving a brownish tint to the panels.<br>' +
			'Mueller reserves the right to add and discontinue colors without notice.';

		closeButton.addEventListener('click', this.closeDisclaimerDetails);

		div.appendChild(closeButton);

		document.body.appendChild(div);
	}

	private openList()
	{
		this._list.classList.remove("hidden");
		if (this._disclaimer)
		{
			this._disclaimer.classList.remove("hidden");
		}

		// scroll to selection
		if (this._selectedOption)
		{
			this._list.scrollTop = this._selectedOption.offsetTop < 300 ? 1000 : 1;
			// top:
			this._list.scroll({
				top: this._selectedOption.offsetTop,
				behavior: 'smooth'
			});
		}
	}

	private onCloseList(event?: MouseEvent)
	{
		if (event)
		{
			const clickedElement = document.elementFromPoint(event.pageX, event.pageY);
			if (clickedElement.id === 'disclaimerDetailsButton' || clickedElement.classList.contains('color-disclaimer'))
			{
				this.showDisclaimerDetails();
				return;
			}
		}

		this._list.removeEventListener("mousedown", this.onListMouseDown);
		window.removeEventListener("mousedown", this.onWindowMouseDown);
		window.removeEventListener("blur", this.onWindowBlur);

		this.closeDisclaimerDetails();
		this.closeList();
	}

	private closeList()
	{
		this._list.classList.add("hidden");
		if (this._disclaimer)
		{
			this._disclaimer.classList.add("hidden");
		}

		this.hideAllNestedOptions();
	}

	private onListClicked = (event:MouseEvent) =>
	{

		var target = this.findParent(event.target, "data-value");
		if (!target)
		{
			return "";
		}



		// check if it has items, if yes -> show nested items

		/*
		var nested = <HTMLElement>target.querySelector("div.addon-nested");
		var items = nested.querySelectorAll("div.addon-nested > div.addon-option");

		if (items.length > 0)
		{
			// has nested items
			nested.style['display'] = "";

			return;
		}
		*/

		// no nested items

		var value = target.getAttribute("data-value");
		if (!value)
		{
			return;
		}

		var prevValue = this._value;
		this.setValueByKey(value, true);
        var nested = false;
        for (var i = 0; i < this._values.array.length; ++i)
        {
            var arritem = this._values.array[i];
            if (value == arritem["id"] && arritem['nested'])
            {
                nested = true;
                break;
            }
        }
        if (!nested)
        {
            if (!this._buttonIsSelected || this._value !== prevValue)
            {
                this.dispatchChange(event);
            }


            this.onCloseList();
        }

	};

	/*
	private findOptionValue(target:Element):string
	{
		var target = this.findParent(event.target, "data-value");
		if (!target)
		{
			return "";
		}

		return target.getAttribute("data-value");
	}
	*/

	private findParent(target:any, attribute:string):Element
	{

		if (!target.hasAttribute)
		{
			return null;
		}

		while (!target.hasAttribute(attribute))
		{
			target = target.parentNode;
			if (!target.hasAttribute)
			{
				return null;
			}
		}

		return <Element>target;
	}

	public dispatchChange(event: Event)
	{
		// TODO cleanup
		this.changeSignal.dispatch(this._value[this._values.keyProperty] || this._value, this, event);
	}

	public setValueByKey(key: string, userInteraction = false)
	{
		// first time we want the label to be "Please Select" but also
		// using a default value
		let value = this._values.getByKey(key);

		// todo this is a hack

		if (value == null)
		{
			if (this.config.id === "wall_liner_panels")
			{
				value = this._values.getByKey('#ffffff');
			}
			else
			{
				value = this._values.getByKey('#d8e5e6');
			}
		}

		if (!userInteraction && Main.getInstance().config.projectSettings === Project.initialElement.settings)
		{
			value = ObjectUtils.clone(value, true);
			value.label =  'Please Select'
		}
		this.value = value;
	}

	public enable()
	{
		this.element.classList.remove("disabled");
	}

	public disable()
	{
		this.element.classList.add("disabled");
	}

	public set value(value:T)
	{
		if (!value)
		{
			return;
		}

		this._value = value;

		if (this._buttonIsSelected)
		{

			var newButton = Templating.renderDOM(this.config.tpl.button, this._value);

			this._button.parentNode.replaceChild(newButton, this._button);

			this._button = newButton;
			this._button.classList.add("selected");
			this._button.addEventListener("click", this.onButtonClicked);

			if (this._selectedOption)
			{
				this._selectedOption.classList.remove("selected");
			}
			var val = value[this._values.keyProperty];
			this._selectedOption = <HTMLElement>this._list.querySelector('div[data-value="'+val+'"]');
			this._selectedOption.classList.add("selected");
		}
	}

	public get value()
	{
		return this._value;
	}

	public get button()
	{
		return this._button;
	}
}
